/**
 * API Service
 * Centralized HTTP client for API requests
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, DEFAULT_HEADERS, buildApiUrl } from '../config/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: DEFAULT_HEADERS,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if this is a login request - let the login page handle the error
      const isLoginRequest = error.config?.url?.includes('/api/auth/login');
      
      if (!isLoginRequest) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Generic API methods
export const api = {
  // GET request
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.get(url, config).then(response => response.data.data || response.data);
  },

  // POST request
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.post(url, data, config).then(response => response.data.data || response.data);
  },

  // PUT request
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.put(url, data, config).then(response => response.data.data || response.data);
  },

  // DELETE request
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.delete(url, config).then(response => response.data.data || response.data);
  },

  // PATCH request
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.patch(url, data, config).then(response => response.data.data || response.data);
  },
};

// Authentication API
export const authApi = {
  login: (email: string, password: string) => {
    return api.post('/api/auth/login', { email, password });
  },

  register: (userData: any) => {
    return api.post('/api/auth/register', userData);
  },

  logout: () => {
    return api.post('/api/auth/logout');
  },

  getMe: () => {
    return api.get('/api/auth/me');
  },

  refreshToken: () => {
    return api.post('/api/auth/refresh');
  },
};

// Companies API
export const companiesApi = {
  getCompanies: () => {
    return api.get('/api/companies');
  },

  getCompany: (id: string) => {
    return api.get(`/api/companies/${id}`);
  },

  createCompany: (data: any) => {
    return api.post('/api/companies', data);
  },

  updateCompany: (id: string, data: any) => {
    return api.put(`/api/companies/${id}`, data);
  },

  deleteCompany: (id: string) => {
    return api.delete(`/api/companies/${id}`);
  },
};

// Locations API
export const locationsApi = {
  getLocations: () => {
    return api.get('/api/locations');
  },

  getLocation: (id: string) => {
    return api.get(`/api/locations/${id}`);
  },

  createLocation: (data: any) => {
    return api.post('/api/locations', data);
  },

  updateLocation: (id: string, data: any) => {
    return api.put(`/api/locations/${id}`, data);
  },

  deleteLocation: (id: string) => {
    return api.delete(`/api/locations/${id}`);
  },
};

// Users API
export const usersApi = {
  getUsers: () => {
    return api.get('/api/users');
  },

  getUser: (id: string) => {
    return api.get(`/api/users/${id}`);
  },

  createUser: (data: any) => {
    return api.post('/api/users', data);
  },

  updateUser: (id: string, data: any) => {
    return api.put(`/api/users/${id}`, data);
  },

  deleteUser: (id: string) => {
    return api.delete(`/api/users/${id}`);
  },
};

// Clients API
export const clientsApi = {
  getClients: () => {
    return api.get('/api/clients');
  },

  getClient: (id: string) => {
    return api.get(`/api/clients/${id}`);
  },

  createClient: (data: any) => {
    return api.post('/api/clients', data);
  },

  updateClient: (id: string, data: any) => {
    return api.put(`/api/clients/${id}`, data);
  },

  deleteClient: (id: string) => {
    return api.delete(`/api/clients/${id}`);
  },
};

// Visits API
export const visitsApi = {
  getVisits: (params?: any) => {
    return api.get('/api/visits', { params });
  },

  getVisit: (id: string) => {
    return api.get(`/api/visits/${id}`);
  },

  createVisit: (data: any) => {
    return api.post('/api/visits', data);
  },

  updateVisit: (id: string, data: any) => {
    return api.put(`/api/visits/${id}`, data);
  },

  deleteVisit: (id: string) => {
    return api.delete(`/api/visits/${id}`);
  },
};

// Treatments API
export const treatmentsApi = {
  getTreatments: () => {
    return api.get('/api/treatments');
  },

  getTreatment: (id: string) => {
    return api.get(`/api/treatments/${id}`);
  },

  getVisitTreatments: (visitId: string) => {
    return api.get(`/api/treatments/visit/${visitId}`);
  },

  createTreatment: (data: any) => {
    return api.post('/api/treatments', data);
  },

  updateTreatment: (id: string, data: any) => {
    return api.put(`/api/treatments/${id}`, data);
  },

  deleteTreatment: (id: string) => {
    return api.delete(`/api/treatments/${id}`);
  },

  getTreatmentPhotos: (id: string) => {
    return api.get(`/api/treatments/${id}/photos`);
  },

  getClientTreatments: (clientId: string, params?: { status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    const queryString = queryParams.toString();
    return api.get(`/api/treatments?clientId=${clientId}${queryString ? `&${queryString}` : ''}`);
  },
};

// Uploads API
export const uploadsApi = {
  uploadPhoto: (formData: FormData) => {
    return api.post('/api/uploads/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getPhoto: (id: string) => {
    return api.get(`/api/uploads/photo/${id}`);
  },

  downloadPhoto: (id: string) => {
    return api.get(`/api/uploads/photo/${id}/download`);
  },

  updatePhoto: (id: string, data: any) => {
    return api.put(`/api/uploads/photo/${id}`, data);
  },

  deletePhoto: (id: string) => {
    return api.delete(`/api/uploads/photo/${id}`);
  },

  getClientPhotos: (clientId: string) => {
    return api.get(`/api/uploads/client/${clientId}/photos`);
  },

  // Profile Picture APIs
  uploadUserProfilePicture: (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post('/api/uploads/profile/user', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadClientProfilePicture: (clientId: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/api/uploads/profile/client/${clientId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getProfilePicture: (type: 'user' | 'client', filename: string) => {
    return api.get(`/api/uploads/profile/${type}/${filename}`);
  },

  deleteProfilePicture: (type: 'user' | 'client', id: string) => {
    return api.delete(`/api/uploads/profile/${type}/${id}`);
  },
};

// Invitations API
export const invitationsApi = {
  getInvitations: (params?: any) => {
    return api.get('/api/invitations', { params });
  },

  getInvitation: (id: string) => {
    return api.get(`/api/invitations/${id}`);
  },

  createInvitation: (data: any) => {
    return api.post('/api/invitations', data);
  },

  updateInvitation: (id: string, data: any) => {
    return api.put(`/api/invitations/${id}`, data);
  },

  deleteInvitation: (id: string) => {
    return api.delete(`/api/invitations/${id}`);
  },

  resendInvitation: (id: string) => {
    return api.post(`/api/invitations/${id}/resend`);
  },

  cancelInvitation: (id: string) => {
    return api.post(`/api/invitations/${id}/cancel`);
  },

  acceptInvitation: (token: string, userData?: any) => {
    return api.post(`/api/invitations/accept/${token}`, userData);
  },

  getInvitationByToken: (token: string) => {
    return api.get(`/api/invitations/token/${token}`);
  },
};

// Location Access API
export const locationAccessApi = {
  getLocationAccess: (locationId?: string) => {
    const url = locationId ? `/api/location-access?location_id=${locationId}` : '/api/location-access';
    return api.get(url);
  },

  getUserLocationAccess: (userId: string) => {
    return api.get(`/api/location-access/user/${userId}`);
  },

  grantLocationAccess: (data: any) => {
    return api.post('/api/location-access', data);
  },

  updateLocationAccess: (id: string, data: any) => {
    return api.put(`/api/location-access/${id}`, data);
  },

  revokeLocationAccess: (id: string) => {
    return api.delete(`/api/location-access/${id}`);
  },

  bulkUpdateLocationAccess: (data: any) => {
    return api.post('/api/location-access/bulk', data);
  },
};

// Email API
export const emailApi = {
  sendEmailVerification: (data: any) => {
    return api.post('/api/email/send-verification', data);
  },

  verifyEmail: (token: string) => {
    return api.post('/api/email/verify', { token });
  },

  sendPasswordReset: (data: any) => {
    return api.post('/api/email/send-password-reset', data);
  },

  resetPassword: (data: { token: string; password: string }) => {
    return api.post('/api/auth/reset-password', data);
  },

  sendOrganizationInvitation: (data: any) => {
    return api.post('/api/email/invitation', data);
  },

  testEmailConfiguration: () => {
    return api.post('/api/email/test');
  },
};

// Company Settings API
export const companyApi = {
  // Company settings
  getSettings: () => {
    return api.get('/api/company/settings');
  },

  updateSettings: (data: any) => {
    return api.put('/api/company/settings', data);
  },

  // Logo management
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post('/api/company/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteLogo: () => {
    return api.delete('/api/company/logo');
  },

  // Business hours
  getBusinessHours: (locationId: string) => {
    return api.get(`/api/company/business-hours/${locationId}`);
  },

  updateBusinessHours: (locationId: string, data: any) => {
    return api.put(`/api/company/business-hours/${locationId}`, data);
  },

  // Default settings
  getDefaultSettings: () => {
    return api.get('/api/company/default-settings');
  },

  updateDefaultSettings: (data: any) => {
    return api.put('/api/company/default-settings', data);
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: () => {
    return api.get('/api/dashboard/stats');
  },
  getPreferences: () => {
    return api.get('/api/dashboard/preferences');
  },
  updatePreferences: (preferences: { config?: any; language?: string }) => {
    // Backend accepts either { language } for language-only updates
    // or { config, language } for combined updates
    return api.put('/api/dashboard/preferences', preferences);
  },
};

// Treatment Types API
export const treatmentTypesApi = {
  getTreatmentTypes: (companyId?: string) => {
    const params = companyId ? { companyId } : {};
    return api.get('/api/treatment-types', { params });
  },

  getTreatmentType: (id: string) => {
    return api.get(`/api/treatment-types/${id}`);
  },

  createTreatmentType: (data: any) => {
    return api.post('/api/treatment-types', data);
  },

  updateTreatmentType: (id: string, data: any) => {
    return api.put(`/api/treatment-types/${id}`, data);
  },

  deleteTreatmentType: (id: string) => {
    return api.delete(`/api/treatment-types/${id}`);
  },
};

// Health API
export const healthApi = {
  getHealth: () => {
    return api.get('/api/health');
  },

  getApiInfo: () => {
    return api.get('/api');
  },
};

// Appointments API
export const appointmentsApi = {
  // Core CRUD
  getAppointments: (params?: any) => {
    return api.get('/api/appointments', { params });
  },
  
  getAppointment: (id: string) => {
    return api.get(`/api/appointments/${id}`);
  },
  
  createAppointment: (data: any) => {
    return api.post('/api/appointments', data);
  },
  
  updateAppointment: (id: string, data: any) => {
    return api.put(`/api/appointments/${id}`, data);
  },
  
  cancelAppointment: (id: string) => {
    return api.delete(`/api/appointments/${id}`);
  },
  
  // Specialized queries
  getTodaysAppointments: () => {
    return api.get('/api/appointments/today');
  },
  
  getUpcomingAppointments: () => {
    return api.get('/api/appointments/upcoming');
  },
  
  getClientAppointments: (clientId: string) => {
    return api.get(`/api/appointments/client/${clientId}`);
  },
  
  getTherapistAppointments: (therapistId: string) => {
    return api.get(`/api/appointments/therapist/${therapistId}`);
  },
  
  // Actions
  completeAppointment: (id: string, createVisit?: boolean) => {
    return api.post(`/api/appointments/${id}/complete`, { createVisit });
  },
  
  confirmAppointment: (id: string) => {
    return api.post(`/api/appointments/${id}/confirm`);
  },
  
  checkAvailability: (params: any) => {
    return api.get('/api/appointments/availability', { params });
  },
  
  // Recurring appointments
  getRecurringAppointments: () => {
    return api.get('/api/appointments/recurring');
  },
  
  createRecurringAppointment: (data: any) => {
    return api.post('/api/appointments/recurring', data);
  },
  
  updateRecurringAppointment: (id: string, data: any) => {
    return api.put(`/api/appointments/recurring/${id}`, data);
  },
  
  deleteRecurringAppointment: (id: string) => {
    return api.delete(`/api/appointments/recurring/${id}`);
  },
  
  generateRecurringInstances: (id: string, data: any) => {
    return api.post(`/api/appointments/recurring/${id}/generate`, data);
  },
  
  // Waiting list
  getWaitingList: (params?: any) => {
    return api.get('/api/appointments/waiting-list', { params });
  },
  
  addToWaitingList: (data: any) => {
    return api.post('/api/appointments/waiting-list', data);
  },
  
  updateWaitingListEntry: (id: string, data: any) => {
    return api.put(`/api/appointments/waiting-list/${id}`, data);
  },
  
  removeFromWaitingList: (id: string) => {
    return api.delete(`/api/appointments/waiting-list/${id}`);
  },
  
  scheduleFromWaitingList: (id: string, appointmentData: any) => {
    return api.post(`/api/appointments/waiting-list/${id}/schedule`, appointmentData);
  },
  
  suggestAvailableSlots: (params: any) => {
    return api.get('/api/appointments/waiting-list/suggestions', { params });
  },
};

// Billing API
export const billingApi = {
  // Subscription
  getSubscription: () => {
    return api.get('/api/billing/subscription');
  },

  getUsage: () => {
    return api.get('/api/billing/usage');
  },

  // Checkout
  createCheckout: (data: { plan: string; billingPeriod: string; discountCode?: string }) => {
    return api.post('/api/billing/checkout', data);
  },

  // Subscription management
  upgrade: (data: { plan: string; billingPeriod: string }) => {
    return api.post('/api/billing/upgrade', data);
  },

  downgrade: (data: { plan: string; billingPeriod: string }) => {
    return api.post('/api/billing/downgrade', data);
  },

  cancel: () => {
    return api.post('/api/billing/cancel');
  },

  // Invoices
  getInvoices: () => {
    return api.get('/api/billing/invoices');
  },

  // Discount codes
  applyDiscount: (discountCode: string) => {
    return api.post('/api/billing/apply-discount', { discountCode });
  },

  // Payment verification
  verifyPayment: (sessionId: string) => {
    return api.get(`/api/billing/verify-payment?session_id=${sessionId}`);
  },
};

export default api;
